/**
 * Customer Announcements — list + detail views.
 * Also exports helpers used by the CustomerHome card.
 */
import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Section, Card, Badge, Button, Alert } from "../design-system";
import { useAuth } from "../auth/AuthProvider";
import { AlertTriangle, Info, Bell, Paperclip, ExternalLink, ChevronLeft } from "lucide-react";

export const PRIORITY_LABEL = {
  URGENT: "Urgente",
  IMPORTANT: "Importante",
  NORMAL: "Normale",
};

export const PRIORITY_TONE = {
  URGENT: "danger",
  IMPORTANT: "warning",
  NORMAL: "info",
};

export function PriorityIcon({ priority, size = 16 }) {
  if (priority === "URGENT") return <AlertTriangle size={size} aria-hidden="true" />;
  if (priority === "IMPORTANT") return <Bell size={size} aria-hidden="true" />;
  return <Info size={size} aria-hidden="true" />;
}

function fmtDate(iso) { return iso ? new Date(iso).toLocaleDateString("it-IT") : ""; }

// ---------------------------------------------------------------------------
// List page
// ---------------------------------------------------------------------------
export function CustomerAnnouncementsPage() {
  const { apiGet } = useAuth();
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    apiGet("/api/customer/announcements").then(setRows).catch((e) => setErr(e?.message || String(e)));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (err) return <Section title="Comunicazioni"><Alert tone="danger">Errore: {err}</Alert></Section>;
  if (!rows) return <Section title="Comunicazioni"><Card><Card.Body className="muted">Caricamento…</Card.Body></Card></Section>;

  return (
    <Section title="Comunicazioni importanti"
             subtitle="Avvisi aziendali e logistici attivi per il tuo account"
             actions={<Link to="/customer"><Button size="sm" data-testid="ann-back-home"><ChevronLeft size={14} /> Home</Button></Link>}
             data-testid="customer-announcements-page">
      {rows.length === 0 ? (
        <Card><Card.Body className="muted">Nessuna comunicazione attiva al momento.</Card.Body></Card>
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          {rows.map((a) => (
            <AnnouncementCard key={a.id} a={a} />
          ))}
        </div>
      )}
    </Section>
  );
}

function AnnouncementCard({ a }) {
  const isUrgent = a.priority === "URGENT";
  return (
    <Card data-testid={`ann-card-${a.id}`}
          style={isUrgent ? { borderLeft: "5px solid #dc2626" } : undefined}>
      <Card.Header>
        <div style={{ display: "flex", justifyContent: "space-between",
                      alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <PriorityIcon priority={a.priority} size={18} />
            <strong>{a.title}</strong>
          </div>
          <Badge tone={PRIORITY_TONE[a.priority] || "info"}>
            {PRIORITY_LABEL[a.priority] || a.priority}
          </Badge>
        </div>
      </Card.Header>
      <Card.Body>
        <p style={{ margin: 0, whiteSpace: "pre-line" }}>{a.summary || ""}</p>
        <div className="muted" style={{ fontSize: ".78rem", marginTop: 8 }}>
          Valido: {fmtDate(a.starts_at)} → {fmtDate(a.ends_at)}
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
          <Link to={`/customer/announcements/${a.id}`}>
            <Button size="sm" data-testid={`ann-open-${a.id}`}>Apri</Button>
          </Link>
          {a.link_url && (
            <a href={a.link_url} target="_blank" rel="noreferrer"
               data-testid={`ann-link-${a.id}`}>
              <Button size="sm" variant="secondary">
                <ExternalLink size={14} /> Link esterno
              </Button>
            </a>
          )}
          {a.has_attachment && (
            <Link to={`/customer/announcements/${a.id}`}>
              <Button size="sm" variant="secondary" data-testid={`ann-attach-${a.id}`}>
                <Paperclip size={14} /> Allegato
              </Button>
            </Link>
          )}
        </div>
      </Card.Body>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Detail page
// ---------------------------------------------------------------------------
export function CustomerAnnouncementDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { apiGet, token } = useAuth();
  const [a, setA] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    apiGet(`/api/customer/announcements/${id}`).then(setA).catch((e) => {
      if (e?.status === 404) setErr("Comunicazione non trovata o non più disponibile.");
      else setErr(e?.message || String(e));
    });
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const downloadAttachment = async () => {
    // Use fetch to include Authorization header, then trigger blob download.
    try {
      const res = await fetch(`${backend}/api/customer/announcements/${id}/attachment`, {
        headers: { Authorization: `Bearer ${token || ""}` },
      });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = a?.attachment_filename || "allegato.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("Impossibile scaricare l'allegato: " + (e?.message || e));
    }
  };

  if (err) return (
    <Section title="Comunicazione"
             actions={<Button size="sm" onClick={() => nav(-1)}><ChevronLeft size={14} /> Indietro</Button>}>
      <Alert tone="danger">{err}</Alert>
    </Section>
  );
  if (!a) return <Section title="Comunicazione"><Card><Card.Body className="muted">Caricamento…</Card.Body></Card></Section>;

  const isUrgent = a.priority === "URGENT";

  return (
    <Section title={a.title}
             subtitle={`${PRIORITY_LABEL[a.priority] || a.priority} · Valido ${fmtDate(a.starts_at)} → ${fmtDate(a.ends_at)}`}
             actions={<Button size="sm" onClick={() => nav(-1)} data-testid="ann-detail-back"><ChevronLeft size={14} /> Indietro</Button>}
             data-testid={`customer-announcement-detail-${a.id}`}>
      <Card style={isUrgent ? { borderLeft: "5px solid #dc2626" } : undefined}>
        <Card.Body>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <PriorityIcon priority={a.priority} size={20} />
            <Badge tone={PRIORITY_TONE[a.priority] || "info"}>
              {PRIORITY_LABEL[a.priority] || a.priority}
            </Badge>
          </div>
          <div style={{ whiteSpace: "pre-line", lineHeight: 1.5 }}>{a.body}</div>
          <div style={{ display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
            {a.link_url && (
              <a href={a.link_url} target="_blank" rel="noreferrer">
                <Button size="sm" variant="secondary" data-testid="ann-detail-link">
                  <ExternalLink size={14} /> Apri link
                </Button>
              </a>
            )}
            {a.has_attachment && (
              <Button size="sm" variant="primary" onClick={downloadAttachment}
                      data-testid="ann-detail-download">
                <Paperclip size={14} /> Scarica allegato ({a.attachment_filename})
              </Button>
            )}
          </div>
        </Card.Body>
      </Card>
    </Section>
  );
}
